import type { Guest } from "@/types/Guest";
import { printedSeatOf, seatPlaceOf } from "@/lib/seating/seatNumber";

/**
 * The seating tokens a preview can resolve from the guest it already holds.
 *
 * `{{seatNumber}}` and friends are ENRICHER-GATED: the pass renderer looks them
 * up server-side from the seating collections at build time
 * (`DocumentTemplatePerGuestService.enrichSeatingPayload`), and no client ever
 * saw them. So the wallet preview drew the builder's em-dash where the
 * delivered pass shows "PLATZ 3" — correct in the sense that the preview could
 * not know, and useless to the operator comparing the two.
 *
 * It CAN know. `seatInfo` is on the guest projection already (`GUEST_FIELDS`),
 * it is stamped from the very same objects, and the rule is the same: the
 * backend writes `seatNumber` = the chair's printed number (`SeatNumbers.of`:
 * the numbering's `number`, else `seatIndex plus one`), `seatRow` = its row label
 * when the piece labels rows, and `tableNumber = furniture.getName()`, while
 * the guest-list lookup stamps `seatInfo.seatNumber` / `seatRow` off the same
 * chair (absent on an older backend — then `seatIndex plus one`, which is what that
 * backend printed). Same source, same fields, no second request.
 *
 * `{{seatBlock}}` / `{{seatArea}}` (seats.io Teil 2, Zug 3) follow `{{seatRow}}`:
 * the same `seatInfo` carries the piece's cached block/area (`seatBlock`/
 * `seatArea`, keys absent when the piece has no block), and the renderer puts
 * them under the same names only then.
 *
 * ONLY THESE TOKENS, and the omissions are deliberate rather than unfinished:
 *  - `seatLabel` is `seat.getLabel()`, which `seatInfo` does not carry.
 *  - `seatStatus` is a HUMANIZED string (`humanizeSeatStatus`); `seatInfo.status`
 *    is the raw enum, and guessing the mapping would print a word the pass does
 *    not use.
 *  - `roomName` / `floorplanUrl` need a room lookup.
 *  - `seatList` is the multi-room aggregate, which is exactly the case this
 *    module refuses to answer (below).
 * Those stay unknown, so the preview keeps its em-dash AND its caption for
 * them — which is the honest answer, not a gap.
 */
export type SeatVariables = {
  seatNumber?: number;
  /** Only when the chair has a row label — the renderer puts the key only then, too. */
  seatRow?: string;
  /**
   * Only when the seat's piece belongs to a block (seats.io Teil 2, Zug 3) —
   * `seatInfo.seatBlock`, the piece's cached block name; the renderer puts the
   * key only then, too, like `seatRow`.
   */
  seatBlock?: string;
  /** …and that block's area, only when it names one. */
  seatArea?: string;
  tableNumber?: string;
  furnitureName?: string;
};

/**
 * @returns the tokens this guest's seat settles, or `{}` when it settles none.
 *
 * REFUSES A GUEST SEATED MORE THAN ONCE — a DEPLOY GUARD, and the only reason
 * it still exists is that production has not caught up yet.
 *
 * The two sides used to disagree about which seat is the primary one:
 * `SeatingGuestService.collectSeats` — which produces `seatInfo` — sorts by
 * (furnitureId, seatIndex) and takes the first, while the pass renderer's
 * `enrichSeatingPayload` took whatever `findByEventAndGuestId` handed back,
 * from a query with no `sort`, under a comment claiming it mirrored
 * `getGuestSeatInfo`. It did not. For one seat the two are trivially the same
 * answer; for a guest seated in two rooms the delivered pass could carry a seat
 * the cockpit never showed.
 *
 * The renderer half is FIXED in main-backend
 * (`DocumentTemplatePerGuestService.primarySeat`, pinned by
 * `SeatEnrichPrimarySeatTest`). Deploying it is NOT enough to remove the guard,
 * and an earlier version of this comment said it was — wrongly, which is worth
 * spelling out because the correction is the useful part:
 *
 * `seatInfo` does not come from `collectSeats` at all. It is stamped by the
 * guest-list `$lookup` in `GuestRepository.buildGuestPipeline`, which is a
 * THIRD rule: `$sort {_id:1} $limit 1` picks the furniture (agreeing with the
 * other two), but the seat within it is `$arrayElemAt($filter(seats, …), 0)` —
 * the first ARRAY element, not the lowest `seatIndex`. So for a guest holding
 * two seats on ONE table whose array order has drifted from index order, the
 * preview and the pass still disagree after the backend ships.
 *
 * TO REMOVE, both of these must be true:
 *  1. main-backend carrying `primarySeat` is live, AND
 *  2. the `$lookup` picks the lowest `seatIndex` within the chosen furniture
 *     (a `$sortArray` on the `$filter` result before `$arrayElemAt 0`).
 * Then delete the `count > 1` branch below and the two tests that pin it (here
 * and the send dialog's "keeps the em-dash for a guest seated in more than one
 * place"). Do NOT remove it on (1) alone.
 *
 * Bounded, but not as tightly as this comment first claimed: `seatCount` is a
 * best-effort rollup written by a SEPARATE update after the furniture write
 * (`GuestActivityRollupService.recomputeSeating`, every caller `…BestEffort`,
 * repaired by a nightly reconcile). It can read 1 while the guest already holds
 * two seats, and that is exactly the window the guard is for. Cheap enough to
 * keep either way.
 *
 * `seatCount` is a rollup stamped on every seat write. Absent (legacy rows, a
 * projection that skipped it) it cannot prove multiplicity, and a lone
 * `seatInfo` is then taken at face value — the em-dash is ALWAYS wrong for a
 * seated guest, so the one-seat reading is the better bet. `> 1` is the only
 * value that refuses.
 *
 * The rollup is also not instantaneous — see the note above on staleness. The
 * guard is cheap, so it stays rather than being argued away.
 */
export function buildSeatVariables(guest: Guest | null | undefined): SeatVariables {
  const info = guest?.seatInfo;
  if (!info) return {};
  const count = guest?.seatCount;
  if (typeof count === "number" && count > 1) return {};

  const out: SeatVariables = {};
  // The PRINTED number: the server's stamp (`number` of a numbered piece),
  // else the 0-based `seatIndex` made 1-based, as the pass always did. `!= null`,
  // not a truthiness test: index 0 is the FIRST seat and the most common one
  // there is, and `if (info.seatIndex)` would have silently dropped exactly it.
  if (info.seatIndex != null && Number.isFinite(info.seatIndex)) {
    const printed = printedSeatOf(info);
    out.seatNumber = printed.seatNumber;
    if (printed.seatRow !== null) out.seatRow = printed.seatRow;
  }
  // The piece's block and its area — keys only when the server stamped them
  // (a seat without a block settles neither, exactly as before).
  const place = seatPlaceOf(info);
  if (place.seatBlock !== undefined) out.seatBlock = place.seatBlock;
  if (place.seatArea !== undefined) out.seatArea = place.seatArea;
  // Both names, because the renderer writes both from one value and a template
  // may carry either.
  if (info.furnitureName) {
    out.tableNumber = info.furnitureName;
    out.furnitureName = info.furnitureName;
  }
  return out;
}

/**
 * The guest with its seat tokens folded in, for substitution and for
 * `knowsToken`. Mirrors `withEventVariables`: a shallow copy, so the cached
 * guest object the query holds is never mutated.
 *
 * The guest's OWN fields win. A `seatNumber` that somehow already exists on the
 * guest (an additional field, a future projection) is the more direct answer
 * and must not be overwritten by a derived one.
 */
export function withSeatVariables<T extends object>(
  guest: T,
  vars: SeatVariables,
): T & SeatVariables {
  return { ...vars, ...guest };
}

/**
 * Every token the PASS RENDERER fills from the seating plan — on both platforms.
 *
 * Wider than `SeatVariables` above on purpose. That type lists what the COCKPIT
 * can derive from `seatInfo`; this set lists what the SERVER will put on the
 * pass once a seat exists, which is a different and larger question. Read off
 * the two enrichers rather than guessed:
 *
 *  - Apple: `DocumentTemplatePerGuestService.enrichSeatingPayload` puts
 *    `tableNumber`, `furnitureName`, `furnitureType`, `seatNumber`, `seatLabel`,
 *    `seatStatus`, `roomName`, `floorplanUrl` once `primarySeat(...)` is
 *    non-null (`seatRow` too when the chair has a row label, `seatBlock`/
 *    `seatArea` when the piece belongs to a block), plus `seatList` when the
 *    template names it.
 *  - Google: `main-consumer`'s `context-enricher.ts` resolves the same family —
 *    `seatList` by its own Mongo lookup gated only on the guest and the token,
 *    and the rest from a `furnitureId` a seating trigger pushed in (that context
 *    is spread AFTER `payloadFromGuest`, so `CANONICAL_KEYS` never filters it).
 *
 * What this set is FOR: it is the one family where an empty value in the
 * preview is not "we cannot know" but a decidable fact, because
 * {@link guestHasSeat} settles it. Everything outside it stays undecidable and
 * must not be presented as either present or absent.
 */
export const SEAT_ENRICHED_KEYS: ReadonlySet<string> = new Set([
  "seatNumber",
  // Put only when the chair has a row label (seats.io slice 1), so a seated
  // guest's `{{seatRow}}` row stays "awaited" (the conservative half), while a
  // guest with NO seat gets "absent" — which is exactly right for this key too.
  "seatRow",
  // Put only when the piece belongs to a block (seats.io Teil 2, Zug 3) — the
  // same reasoning as `seatRow`: awaited for a seated guest, absent without a seat.
  "seatBlock",
  "seatArea",
  "tableNumber",
  "furnitureName",
  "furnitureType",
  "seatLabel",
  "seatStatus",
  "roomName",
  "floorplanUrl",
  "seatList",
]);

/**
 * Does this guest hold a seat at all?
 *
 * The discriminator the preview was missing. `buildSeatVariables` returns `{}`
 * for BOTH "no seat" and "more than one seat", and those two are opposites as
 * far as the delivered pass is concerned:
 *
 *  - no seat  → `primarySeat(...)` is null, the enricher puts nothing, and
 *    `PassJsonBuilder.putSection` deletes the row (configured value, empty
 *    result). The pass carries no such row.
 *  - many seats → the enricher picks a primary and fills it. The pass DOES
 *    carry the row; only this client declines to say which seat it will be.
 *
 * `seatCount` first because it is the honest total (`seatInfo` is only the
 * primary seat); `seatInfo` as the fallback for a legacy row or a projection
 * that skipped the rollup, mirroring `buildSeatVariables`' own tolerance.
 */
export function guestHasSeat(guest: Guest | null | undefined): boolean {
  if (!guest) return false;
  // `seatInfo` FIRST, matching `buildSeatVariables` above. `seatCount` is a
  // rollup and can lag; reading it first let one card resolve `{{tableNumber}}`
  // from a live `seatInfo` and, one row lower, delete `{{seatLabel}}` because
  // the stale rollup said zero — two opposite claims about the same guest.
  if (guest.seatInfo != null) return true;
  return typeof guest.seatCount === "number" && guest.seatCount > 0;
}

/**
 * WHY THIS IS NOT TRI-STATE, since the obvious objection is right and the
 * obvious fix is wrong.
 *
 * The objection: a caller whose projection never asked for `seatInfo` gets
 * `false` here, indistinguishable from a guest who truly holds no seat — and
 * that was a live defect. The send dialog fetched companions with a narrow
 * field list carrying neither key, so a seated companion's seat rows were
 * reported to the operator as missing from their pass, on the screen right
 * before Send.
 *
 * The obvious fix — return `undefined` when neither key is an own property —
 * is worse, and measurably so. `seatInfo` is stamped by the guest-list lookup
 * at READ time and `seatCount` is written on seat writes, so a guest who has
 * never been seated carries NEITHER key. That is the commonest guest in most
 * events. A tri-state would answer `undefined` for exactly them, the classifier
 * would fall back to "a value is coming", and the bar would be back over every
 * unseated guest's seat row — the defect this whole wave exists to remove,
 * reinstated by the guard meant to prevent a different one.
 *
 * So the answer is a plain boolean and the obligation sits on the CALLER: a
 * lane that asks this question must project `seatInfo`. Both guest lanes do
 * (`GUEST_FIELDS`), and `AttachmentPreview`'s companion fetch now does too —
 * pinned by a test, because the next narrow projection is the one that forgets.
 */

/**
 * Tokens whose presence on the pass follows an ID ROLLUP the cockpit holds.
 *
 * The second decidable family, and it is decidable for the same reason the
 * seat one is: the renderer resolves these names from a list of ids, and
 * `DocumentTemplatePerGuestService.resolveNamesInto` returns early on an empty
 * or absent list — so the key never lands, the value resolves to nothing and
 * `PassJsonBuilder.putSection` deletes the row. The consumer's
 * `enrichGuestActivity` is gated the same way.
 *
 * `knowsToken` refuses these unconditionally (they are in `ACTIVITY_NAME_KEYS`),
 * so every one of them arrives as `uncertain` on every guest — and treating
 * that as "a value is coming" promises a row the pass drops whenever the
 * rollup is empty. The rollups are all in `GUEST_ROLLUP_FIELDS`, which both
 * guest lanes fetch, so the client can simply look.
 *
 * `emailedCampaigns` is deliberately ABSENT: its filler could not be located in
 * the backend, and guessing which rollup feeds it would be the same unmeasured
 * claim this map exists to replace. It stays undecidable.
 */
export const ACTIVITY_ROLLUP_FOR: Readonly<Record<string, keyof Guest>> = {
  seatRooms: "seatRoomIds" as keyof Guest,
  apptOfferings: "apptOfferingIds" as keyof Guest,
  apptTreatments: "apptTreatmentIds" as keyof Guest,
  apptLanes: "apptLaneIds" as keyof Guest,
  emailedTemplates: "emailedTemplateIds" as keyof Guest,
};

/**
 * Does the rollup behind an activity token say the guest has none?
 *
 * Same tri-state as {@link guestHasSeat}: `undefined` when the projection did
 * not carry the rollup, because "not fetched" and "empty" must not decide the
 * same way.
 */
export function activityRollupEmpty(
  guest: Guest | null | undefined,
  token: string,
): boolean | undefined {
  const field = ACTIVITY_ROLLUP_FOR[token];
  if (!guest || !field) return undefined;
  if (!Object.prototype.hasOwnProperty.call(guest, field)) return undefined;
  const value = guest[field];
  if (value == null) return true;
  return Array.isArray(value) ? value.length === 0 : false;
}
